import type { ChatterLevel } from '@chessmate/persona';
import { Button, Card, Segmented } from '@chessmate/ui';
import { useMemo, useState } from 'react';
import { PageHeader, PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { useT } from '../i18n/useT.js';
import { gateAnswer, makeGateQuestion } from '../lib/parent-gate.js';
import { effectiveAudio, useAudioSettingsStore } from '../state/audio-settings-store.js';

/**
 * The sound settings screen (spec §Settings — `docs/proposals/ai-personas-voice-and-sound.md`).
 *
 * Two halves:
 *
 *  - **Sound** — what the player hears: the volumes, mute, the switches, how chatty the personas
 *    are and the sensory-friendly preset.
 *  - **Grown-ups** — the locks for this device (voices, chatter, music) and the volume cap,
 *    behind an arithmetic gate.
 *
 * A locked setting is enforced in the EFFECTIVE settings (`effectiveAudio`), not by hiding a
 * control: the control stays visible and disabled, with a note that says who locked it, so a
 * child is not left guessing why the music will not start.
 */

export interface SettingsScreenProps {
  readonly onExit: () => void;
}

const CHATTER_OPTIONS: ReadonlyArray<ChatterLevel> = ['off', 'little', 'lot'];

const CAP_OPTIONS = ['none', 'half', 'quarter'] as const;

type CapValue = (typeof CAP_OPTIONS)[number];

function capFromValue(value: CapValue): number | null {
  if (value === 'half') return 0.5;
  if (value === 'quarter') return 0.25;
  return null;
}

function valueFromCap(cap: number | null): CapValue {
  if (cap === null) return 'none';
  if (cap <= 0.25) return 'quarter';
  return 'half';
}

export function SettingsScreen({ onExit }: SettingsScreenProps) {
  const { t } = useT('settings');
  const { t: tCommon } = useT('common');
  const settings = useAudioSettingsStore();
  const eff = effectiveAudio(settings);

  // The gate is per visit: unlocking is not remembered, so a child cannot walk back in.
  const [gateOpen, setGateOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [answer, setAnswer] = useState('');
  const [wrong, setWrong] = useState(false);
  const question = useMemo(() => makeGateQuestion(), []);

  const cap = settings.locks.volumeCap;
  const submit = () => {
    if (!gateAnswer(question, answer)) {
      setWrong(true);
      setAnswer('');
      return;
    }
    setWrong(false);
    setUnlocked(true);
  };

  return (
    <ScreenLayout width="wide" data-appdom-scope="settings">
      <PageHeader
        title={t('title')}
        spot={{ src: '/art/spot-sound.webp', alt: tCommon('art-alt-sound') }}
        titleTestId="settings-title"
        titleProps={{ 'data-appdom-id': 'settings-title', 'data-appdom-type': 'Text' }}
        action={
          <Button
            type="button"
            variant="secondary"
            onClick={onExit}
            data-testid="settings-back"
            data-appdom-id="settings-back"
            data-appdom-type="Button"
          >
            {tCommon('back')}
          </Button>
        }
      />
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2 lg:gap-8">
      <Card
        className={`${PANEL_CLASS} flex flex-col gap-5 p-5 sm:p-7`}
        data-testid="settings-sound"
        data-appdom-id="settings-sound"
        data-appdom-type="Card"
      >
        <h2
          className="font-display text-h2 font-bold text-fg"
          data-appdom-id="settings-sound-heading"
          data-appdom-type="Text"
        >
          {t('sound-heading')}
        </h2>

        <VolumeRow
          testId="settings-master-volume"
          label={t('master-volume')}
          description={cap === null ? undefined : t('master-volume-capped', { cap: Math.round(cap * 100) })}
          value={eff.master}
          max={cap ?? 1}
          onChange={settings.setMasterVolume}
        />
        <VolumeRow
          testId="settings-sfx-volume"
          label={t('sfx-volume')}
          value={settings.sfxVolume}
          onChange={settings.setSfxVolume}
        />
        <VolumeRow
          testId="settings-music-volume"
          label={t('music-volume')}
          value={settings.musicVolume}
          onChange={settings.setMusicVolume}
        />
        <VolumeRow
          testId="settings-voice-volume"
          label={t('voice-volume')}
          value={settings.voiceVolume}
          onChange={settings.setVoiceVolume}
        />

        <ToggleRow
          testId="settings-mute"
          label={t('mute')}
          description={t('mute-hint')}
          on={settings.muted}
          onChange={settings.setMuted}
        />
        <ToggleRow
          testId="settings-sfx-on"
          label={t('sfx-on')}
          on={settings.sfxOn}
          onChange={settings.setSfxOn}
        />
        <ToggleRow
          testId="settings-music-on"
          label={t('music-on')}
          description={t('music-on-hint')}
          on={settings.musicOn && !settings.locks.music && !settings.sensory}
          onChange={settings.setMusicOn}
          disabled={settings.locks.music || settings.sensory}
          note={
            settings.locks.music
              ? { testId: 'settings-locked-note-music', text: t('locked') }
              : settings.sensory
                ? { testId: 'settings-preset-note-music', text: t('sensory-music-note') }
                : undefined
          }
        />
        <ToggleRow
          testId="settings-voice-on"
          label={t('voice-on')}
          description={t('voice-on-hint')}
          on={eff.voiceOn}
          onChange={settings.setVoiceOn}
          disabled={settings.locks.voices}
          note={settings.locks.voices ? { testId: 'settings-locked-note-voice', text: t('locked') } : undefined}
        />
        <ToggleRow
          testId="settings-narrator-on"
          label={t('narrator-on')}
          description={t('narrator-on-hint')}
          on={eff.narratorOn}
          onChange={settings.setNarratorOn}
          disabled={settings.locks.voices}
          note={settings.locks.voices ? { testId: 'settings-locked-note-narrator', text: t('locked') } : undefined}
        />

        <div
          className="flex flex-col gap-2"
          data-appdom-id="settings-chatter"
          data-appdom-type="Section"
        >
          <span className="text-sm font-bold text-ink" data-appdom-id="settings-chatter-label" data-appdom-type="Text">
            {t('chatter')}
          </span>
          <Segmented
            options={CHATTER_OPTIONS.map((o) => ({ value: o, label: t(`chatter-${o}`) }))}
            value={eff.chatter}
            onChange={settings.setChatter}
            testid="settings-chatter"
            ariaLabel={t('chatter')}
            disabled={settings.locks.chatter || settings.sensory}
          />
          {settings.locks.chatter ? (
            <SettingNote testId="settings-locked-note-chatter" text={t('locked')} />
          ) : settings.sensory ? (
            <SettingNote testId="settings-preset-note-chatter" text={t('sensory-chatter-note')} />
          ) : null}
        </div>

        <ToggleRow
          testId="settings-sensory"
          label={t('sensory')}
          description={t('sensory-hint')}
          on={settings.sensory}
          onChange={settings.setSensory}
        />
      </Card>

      <Card
        className={`${PANEL_CLASS} flex flex-col gap-4 p-5 sm:p-7`}
        data-testid="settings-grown-ups"
        data-appdom-id="settings-grown-ups"
        data-appdom-type="Card"
      >
        <div className="flex items-center gap-4">
          <img src="/art/spot-grownups.webp" alt={tCommon('art-alt-grownups')} className="size-16 shrink-0 rounded-2xl object-cover sm:size-20" />
          <div className="flex min-w-0 flex-col [overflow-wrap:anywhere]">
            <h2
              className="font-display text-h2 font-bold text-fg"
              data-appdom-id="settings-grown-ups-heading"
              data-appdom-type="Text"
            >
              {t('grown-ups-heading')}
            </h2>
            <p className="text-base text-fg-muted" data-appdom-id="settings-grown-ups-hint" data-appdom-type="Text">
              {t('grown-ups-hint')}
            </p>
          </div>
        </div>

        {!unlocked ? (
          gateOpen ? (
            <div
              className="flex flex-col gap-3"
              data-testid="parent-gate"
              data-appdom-id="parent-gate"
              data-appdom-type="Panel"
            >
              <p
                className="text-base font-bold text-ink"
                data-testid="parent-gate-question"
                data-appdom-id="parent-gate-question"
                data-appdom-type="Text"
              >
                {t('gate-question', { a: question.a, b: question.b })}
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={answer}
                data-testid="parent-gate-answer"
                data-appdom-id="parent-gate-answer"
                data-appdom-type="TextInput"
                aria-label={t('gate-answer-label')}
                onChange={(e) => {
                  setAnswer(e.target.value);
                  setWrong(false);
                }}
                className="h-11 w-40 rounded-xl border border-border bg-surface px-4 text-base text-fg focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-ring"
              />
              {wrong ? (
                <p
                  className="text-sm font-bold text-red-600"
                  data-testid="parent-gate-error"
                  data-appdom-id="parent-gate-error"
                  data-appdom-type="Alert"
                  role="alert"
                >
                  {t('gate-wrong')}
                </p>
              ) : null}
              <Button
                type="button"
                onClick={submit}
                data-testid="parent-gate-submit"
                data-appdom-id="parent-gate-submit"
                data-appdom-type="Button"
              >
                {t('gate-submit')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setGateOpen(true)}
              data-testid="parent-gate-open"
              data-appdom-id="parent-gate-open"
              data-appdom-type="Button"
            >
              {t('unlock')}
            </Button>
          )
        ) : (
          <div
            className="flex flex-col gap-4"
            data-testid="parent-locks"
            data-appdom-id="parent-locks"
            data-appdom-type="Panel"
          >
            <ToggleRow
              testId="parent-lock-voices"
              label={t('lock-voices')}
              description={t('lock-voices-hint')}
              on={settings.locks.voices}
              onChange={settings.lockVoices}
            />
            <ToggleRow
              testId="parent-lock-chatter"
              label={t('lock-chatter')}
              description={t('lock-chatter-hint')}
              on={settings.locks.chatter}
              onChange={settings.lockChatter}
            />
            <ToggleRow
              testId="parent-lock-music"
              label={t('lock-music')}
              description={t('lock-music-hint')}
              on={settings.locks.music}
              onChange={settings.lockMusic}
            />
            <div
              className="flex flex-col gap-2"
              data-appdom-id="parent-volume-cap-group"
              data-appdom-type="Section"
            >
              <span
                className="text-sm font-bold text-ink"
                data-appdom-id="parent-volume-cap-label"
                data-appdom-type="Text"
              >
                {t('volume-cap')}
              </span>
              <Segmented
                options={CAP_OPTIONS.map((o) => ({ value: o, label: t(`volume-cap-${o}`) }))}
                value={valueFromCap(cap)}
                onChange={(v) => settings.setVolumeCap(capFromValue(v))}
                testid="parent-volume-cap"
                ariaLabel={t('volume-cap')}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={settings.clearLocks}
              data-testid="parent-locks-clear"
              data-appdom-id="parent-locks-clear"
              data-appdom-type="Button"
            >
              {t('clear-locks')}
            </Button>
          </div>
        )}
      </Card>

      </div>
    </ScreenLayout>
  );
}

// The prop is `testId` (camelCase), not `testid`: `scripts/testid-audit.mjs` only recognises
// `data-testid="…"`, `testId="…"` and `getByTestId('…')`, so a lowercase `testid` prop would make
// every id these rows render look like drift to the CI gate.
function VolumeRow({
  testId,
  label,
  description,
  value,
  max,
  onChange,
}: {
  readonly testId: string;
  readonly label: string;
  readonly description?: string | undefined;
  readonly value: number;
  readonly max?: number | undefined;
  readonly onChange: (next: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label
        className="flex items-center justify-between gap-3 text-base font-bold text-ink"
        htmlFor={testId}
        data-appdom-id={`${testId}-label`}
        data-appdom-type="Text"
      >
        <span>{label}</span>
        <span className="text-sm font-bold text-ink/80">{Math.round(value * 100)}%</span>
      </label>
      <input
        id={testId}
        type="range"
        min={0}
        max={max ?? 1}
        step={0.05}
        value={value}
        aria-label={label}
        data-testid={testId}
        data-appdom-id={testId}
        data-appdom-type="FormField"
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-12 min-h-[48px] w-full cursor-pointer accent-accent"
      />
      {description ? <span className="text-sm text-ink/80">{description}</span> : null}
    </div>
  );
}

function ToggleRow({
  testId,
  label,
  description,
  on,
  onChange,
  disabled,
  note,
}: {
  readonly testId: string;
  readonly label: string;
  readonly description?: string | undefined;
  readonly on: boolean;
  readonly onChange: (next: boolean) => void;
  readonly disabled?: boolean | undefined;
  /** Why the control is off limits — a grown-up's lock, or the sensory-friendly preset. */
  readonly note?: { readonly testId: string; readonly text: string } | undefined;
}) {
  return (
    <div className="flex min-h-[48px] items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col [overflow-wrap:anywhere]">
        <span className="text-base font-bold text-ink" data-appdom-id={`${testId}-label`} data-appdom-type="Text">
          {label}
        </span>
        {description ? (
          <span className="text-sm text-ink/80" data-appdom-id={`${testId}-hint`} data-appdom-type="Text">
            {description}
          </span>
        ) : null}
        {note ? <SettingNote testId={note.testId} text={note.text} /> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        data-testid={testId}
        data-appdom-id={testId}
        data-appdom-type="Toggle"
        data-on={on ? 'true' : 'false'}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className="relative h-12 w-16 shrink-0 rounded-full disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span aria-hidden="true" className={`absolute inset-x-1 top-2 h-8 rounded-full transition-colors ${on ? 'bg-accent' : 'bg-ink/20'}`} />
        <span
          aria-hidden="true"
          className={`absolute left-1 top-3 h-6 w-6 rounded-full bg-cream shadow transition-transform ${
            on ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * The note under a control that is off limits. It says WHO decided, so the child is not left
 * guessing why the music will not start.
 */
function SettingNote({ testId, text }: { readonly testId: string; readonly text: string }) {
  return (
    <span
      className="text-sm font-bold text-berry"
      data-testid={testId}
      data-appdom-id={testId}
      data-appdom-type="Text"
    >
      {text}
    </span>
  );
}
